import React, { useCallback, useMemo, useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import '../../resources/css/common/Dropzone.scss';
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import * as pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import Loading from './Loading';
import rotateIcon from '../../resources/img/ico_rotate.png';

const baseStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  transition: 'border .3s ease-in-out',
  width: 'auto',
  marginLeft: '3px',
};

const activeStyle = {
  borderColor: '#2196f3'
};

const acceptStyle = {
  borderColor: '#00e676'
};

const rejectStyle = {
  borderColor: '#ff1744'
};

export const DropzoneComponent = ({  callback, title, subTitle, type, modalMessage, index, interiorGraphImgUrl, setAnyImageRemoved, openModalAlert }) => {
  const canvasRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const[imgUrl, setImgUrl] = useState(interiorGraphImgUrl);

  // convert any data url to a file (for pdf to image conversion)
  function dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(','), mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  const onDrop = useCallback(acceptedFiles => {
    setLoading(true);

    if(acceptedFiles.length > 1) {
      if(modalMessage !== 'add-multi-floor') {
        openModalAlert("Each floor should have only one image.");
        setLoading(false);
        return;
      }
    }

    if (modalMessage !== 'add-multi-floor') {
      setFiles([]);
      setImgUrl('');
    }

    const convertPdfToImage = async () => {
      for (const file of acceptedFiles) {
        const isValid = await checkMimeType(file);
        if (!isValid) {
          openModalAlert("One or more files are not valid. Please upload only real JPG, PNG, or PDF files.");
          setLoading(false);
          return;
        }
      }

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

      const imageFiles = [];

      for (const file of acceptedFiles) {
        if (file.type === "application/pdf") {
          const url = URL.createObjectURL(file);
          const pdf = await pdfjsLib.getDocument(url).promise;

          // Check for multi-page PDF in single image mode
          if (modalMessage !== 'add-multi-floor') {
            if(pdf.numPages === 1) {

              const page = await pdf.getPage(1);
              const viewport = page.getViewport({scale: 1.5});
  
              canvas.width = viewport.width;
              canvas.height = viewport.height;
  
              const renderContext = {
                canvasContext: context,
                viewport: viewport,
              };
  
              await page.render(renderContext).promise;
  
              const imageData = canvas.toDataURL('image/png');
              const imageFile = dataURLtoFile(imageData, file.name);
              imageFile.preview = imageData;
              imageFile.rotation = 0;
              imageFiles.push(imageFile);
  
            } else {
              openModalAlert(`The PDF file has many pages but each floor must have only one image`);
              setLoading(false);
              return;
            }
          }

          if (modalMessage === 'add-multi-floor'){
            for (let pageNum = 0; pageNum < pdf.numPages; pageNum++) {
              const page = await pdf.getPage(pageNum + 1);
              const viewport = page.getViewport({scale: 1.5});

              canvas.width = viewport.width;
              canvas.height = viewport.height;

              const renderContext =  {
                canvasContext: context,
                viewport: viewport,
              };

              await page.render(renderContext).promise;

              const imageData = canvas.toDataURL('image/png');
              const imageFile = dataURLtoFile(imageData, acceptedFiles[0].name);

              imageFile.preview = imageData;
              imageFile.rotation = 0;
              imageFiles.push(imageFile);
            }
          }
        } else {
          const preview = URL.createObjectURL(file);
          file.preview = preview;
          file.rotation = 0;
          imageFiles.push(file)
        }
      }
      setLoading(false);

      setFiles(prev => {
        if (prev.length === 0) {
          return imageFiles;
        } else {
          const updated = [...prev];
          updated.splice(index + 1, 0, ...imageFiles);
          return updated;
        }
      });
    }

    convertPdfToImage();

  }, [openModalAlert, modalMessage, index]);

  const fileSizeValidator = (file) => {
    if (file.size > 10000000) {  // max size: 10MB
      return {
        code: "file-size-too-large",
        message: `File size is too large, maximum file size is 10MB`
      };
    }
    return null;
  }

  const checkMimeType = async (file) => {
    const blob = file.slice(0, 4);
    const arrayBuffer = await blob.arrayBuffer();
    const uint = new Uint8Array(arrayBuffer);

    const hex = [...uint].map(b => b.toString(16).padStart(2, '0')).join('');
    const signatures = {
      jpg: ['ffd8ffe0', 'ffd8ffe1', 'ffd8ffe2'],
      png: ['89504e47'],
      pdf: ['25504446'],
    };

    return (
        signatures.jpg.includes(hex) ||
        signatures.png.includes(hex) ||
        signatures.pdf.includes(hex)
    );
  };

  const rotateImage = (index) => {
    setFiles(prevFiles => {
      const updatedFiles = [...prevFiles];
      const file = updatedFiles[index];
      updatedFiles[index] = {
        ...file,
        rotation: ((file.rotation || 0) + 90) % 9000,
        name: file.name,
        type: file.type,
      };
      return updatedFiles;
    });
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
    fileRejections
  } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.png'],
      'application/pdf': ['.pdf']
    },
    validator: fileSizeValidator
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <div key={file.path}>
        {errors.map(e => (
          <div key={e.code} style={{color: 'red'}}><label className='need-star-label'>*</label>{e.message}</div>
        ))}
    </div>
  ));

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {}),
    height: '280px',
  }), [
    isDragActive,
    isDragReject,
    isDragAccept
  ]);

  const remove = (index) => {
    setFiles(prev => {
      const removed = prev[index];
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }

      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    })

  }

  const displayCurrentFile = () => {
    return (
        <>
          <div
              style={{
                justifyContent: 'center',
                display: 'flex',
                backgroundImage: `url('data:image/png;base64,${imgUrl}')`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                width: '500px',
                height: '230px'
              }}
          ></div>

          <div style={{justifyContent: 'center', display: 'flex'}}>
              <span className={'area-file-remove'} onClick={(e) => {
                e.stopPropagation();
                setImgUrl('');
                setAnyImageRemoved();
              }}>Remove selected file</span>
          </div>
        </>
    )
  }

  const updateNewFiles = (files) => {
    let maxWidth = '500px';

    return files.map((file, index) => {
      return (
          <>
            <div className={type === "large" ? 'lg-section' : 'sm-section'} key={file.name}>

              <dix style={{justifyContent: 'center'}}>
                <img
                    src={files[index].preview}
                    alt={files[index].name}
                    style={{
                      maxWidth: maxWidth,
                      maxHeight: '230px',
                      height: 'auto',
                      width: '100%'
                    }}
                />
              </dix>

              <div style={{justifyContent: 'center', display: 'flex'}}>
                <span className={'area-file-remove'} onClick={(e) => {
                  e.stopPropagation();
                  remove(index);
                }}>Remove selected file</span>
              </div>

            </div>
          </>
      );
    });
  }

  const getFile = (files, callback) => {
    callback(files);
    return imgUrl ? displayCurrentFile() : updateNewFiles(files);

  }

  const displayFile = (files, callback, index) => {
    callback(files);
    const maxHeight = 230;
    const maxWidth = 500;
  
    if (files.length > 0 && index >= 0 && index < files.length) {
      const rotation = (files[index].rotation || 0) % 360;
      const isVertical = rotation === 90 || rotation === 270;
  
      const containerWidth = isVertical ? maxHeight : maxWidth;
  
      return (
        <div
          className={type === "large" ? 'lg-section' : 'sm-section'}
          key={files[index].name}
          style={{ position: 'relative' }}
        >
          <div
            style={{
              width: `${containerWidth}px`,
              height: maxHeight,
              overflow: 'hidden',
              margin: '0 auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <img
              src={files[index].preview}
              alt={files[index].name}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                transform: `rotate(${files[index].rotation || 0}deg)`,
                transition: 'transform 0.2s',
              }}
            />
          </div>
          <span
            id="import-floor-plan-rotate-btn"
            onClick={(e) => {
              e.stopPropagation();
              rotateImage(index);
            }}
            className="rotate-button"
            style={{
              right: isVertical ? '-150px' : '-15px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#e6f7ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
            title="Rotate"
          >
            <img
              src={rotateIcon}
              style={{ width: '30px', height: '30px' }}
              alt=""
              draggable={false}
            />
          </span>
  
          <div style={{ justifyContent: 'center', display: 'flex'}}>
            <span
              className={'area-file-remove'}
              onClick={(e) => {
                e.stopPropagation();
                remove(index);
              }}
            >
              Remove selected file
            </span>
          </div>
        </div>
        
      );
    }

    return null;
  };

  return (
      <section className='area-section'>
        <div>
          <div {...getRootProps({style})}>

            {imgUrl ? <></> :
                <>
                  <input {...getInputProps()} />
                  <div className='area-upload-sign'>
                    {files.length > 0 ? '' : <><p className='area-upload-sign-logo'>{title}</p>
                      <p className='area-upload-sign-size'>{subTitle}</p></>
                    }
                    <canvas style={{display: 'none'}} ref={canvasRef}/>
                  </div>
                </>
            }

            <aside>
              <Loading loading={loading}></Loading>
              {modalMessage !== 'add-multi-floor' ? getFile(files, callback) : displayFile(files, callback, index)}
            </aside>

            <span>{fileRejectionItems}</span>

          </div>
        </div>
      </section>
  )
}

export default DropzoneComponent;